import { useState, useEffect } from 'react';
import { useFile, useSelection, useVisualizer } from '../../context/AppContext.jsx';
import { useMirror } from '../../hooks/useMirror.js';

export default function MirrorSection() {
  const { nodes, currentFeatures } = useFile();
  const { multiSelectedIndices } = useSelection();
  const { visualizer } = useVisualizer();
  const { applyMirror } = useMirror();
  const [scope, setScope] = useState('all');
  const [selectedNode, setSelectedNode] = useState('');
  const [selectedFeature, setSelectedFeature] = useState('Base');
  const [mirrorPlane, setMirrorPlane] = useState('xz');
  const [createNew, setCreateNew] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Show/hide mirror plane visualization when section is expanded/collapsed
  useEffect(() => {
    if (visualizer) {
      if (isExpanded) {
        // Find the selected feature
        const feature = currentFeatures.find(f => f.name === selectedFeature);
        if (feature) {
          visualizer.showMirrorPlane(feature, mirrorPlane);
        }
      } else {
        visualizer.hideMirrorPlane();
      }
    }
  }, [isExpanded, selectedFeature, mirrorPlane, currentFeatures, visualizer]);

  // Update plane visualization when feature or plane changes
  useEffect(() => {
    if (visualizer && isExpanded) {
      const feature = currentFeatures.find(f => f.name === selectedFeature);
      if (feature) {
        visualizer.showMirrorPlane(feature, mirrorPlane);
      }
    }
  }, [selectedFeature, mirrorPlane, currentFeatures, visualizer, isExpanded]);

  // Clean up plane visualization when component unmounts
  useEffect(() => {
    return () => {
      if (visualizer) {
        visualizer.hideMirrorPlane();
      }
    };
  }, [visualizer]);

  const handleApplyMirror = () => {
    const nodeIndex = scope === 'node' ? parseInt(selectedNode) : null;
    if (scope === 'node' && (isNaN(nodeIndex) || nodeIndex < 0)) {
      return;
    }
    applyMirror(scope, nodeIndex, selectedFeature, mirrorPlane, createNew);
  };

  return (
    <section className="mb-6">
      <h2
        className="text-lg font-semibold mb-4 pb-2 border-b-2 border-border cursor-pointer flex items-center justify-between hover:text-primary transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <span className="flex items-center gap-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
          </svg>
          <span>Mirror Tool</span>
        </span>
        <span className={`text-base text-text-secondary transition-transform ${isExpanded ? '' : '-rotate-90'}`}>▼</span>
      </h2>

      {isExpanded && (
        <>
          <p className="text-text-secondary text-sm mb-4">Mirror waypoints about a feature plane</p>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-semibold text-text-secondary mb-1 block">Apply to:</label>
              <select
                value={scope}
                onChange={(e) => {
                  setScope(e.target.value);
                  setSelectedNode('');
                }}
                className="w-full bg-bg-tertiary border border-border rounded px-2 py-1.5 text-text-primary text-sm"
              >
                <option value="all">All Waypoints</option>
                <option value="selected">Selected Waypoints</option>
                <option value="node">Node</option>
              </select>
            </div>

            {scope === 'node' && (
              <div>
                <label className="text-sm font-semibold text-text-secondary mb-1 block">Node:</label>
                <select
                  value={selectedNode}
                  onChange={(e) => setSelectedNode(e.target.value)}
                  className="w-full bg-bg-tertiary border border-border rounded px-2 py-1.5 text-text-primary text-sm"
                >
                  <option value="">Select a node...</option>
                  {nodes.map((node, index) => (
                    <option key={index} value={index}>
                      {node.type} - {node.feature} ({node.indices.length} waypoints)
                    </option>
                  ))}
                </select>
              </div>
            )}

            {scope === 'selected' && (
              <div className="text-sm text-text-secondary">
                {multiSelectedIndices.size > 0 ? (
                  <span>{multiSelectedIndices.size} waypoint(s) selected</span>
                ) : (
                  <span className="text-error">No waypoints selected. Use Ctrl+Click to select multiple.</span>
                )}
              </div>
            )}

            <div>
              <label className="text-sm font-semibold text-text-secondary mb-1 block">Mirror Feature:</label>
              <select
                value={selectedFeature}
                onChange={(e) => setSelectedFeature(e.target.value)}
                className="w-full bg-bg-tertiary border border-border rounded px-2 py-1.5 text-text-primary text-sm"
              >
                {currentFeatures.map((feature, index) => (
                  <option key={index} value={feature.name}>
                    {feature.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm font-semibold text-text-secondary mb-1 block">Mirror Plane:</label>
              <select
                value={mirrorPlane}
                onChange={(e) => setMirrorPlane(e.target.value)}
                className="w-full bg-bg-tertiary border border-border rounded px-2 py-1.5 text-text-primary text-sm"
              >
                <option value="xy">XY Plane</option>
                <option value="xz">XZ Plane (Default)</option>
                <option value="yz">YZ Plane</option>
              </select>
            </div>

            <div>
              <label className="text-sm font-semibold text-text-secondary mb-1 block">Operation:</label>
              <select
                value={createNew ? 'create' : 'move'}
                onChange={(e) => setCreateNew(e.target.value === 'create')}
                className="w-full bg-bg-tertiary border border-border rounded px-2 py-1.5 text-text-primary text-sm"
              >
                <option value="create">Create New Waypoints</option>
                <option value="move">Move Existing Waypoints</option>
              </select>
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleApplyMirror}
                className="flex-1 bg-primary text-white px-3 py-1.5 text-sm rounded hover:bg-blue-600 transition-colors"
              >
                Apply Mirror
              </button>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
